import React, { useContext, useEffect, useRef, useState } from "react";
import {
  NavLink,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { zeosConfirmation } from "../../../components/Shared/confirm-dialog/ConfirmDialog";
import { FeatureFlags } from "../../../configs/FeatureFlags";
import { AuthContext } from "../../../context/AuthContext";
import { useSignIn } from "../../../hooks/useSignIn";
import { createLogger } from "../../../services/Logger";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import { ICompanyClaims } from "../../../services/zeos-api/contracts/Account";
import { managesMultiplePortfolios } from "../../../services/helpers/AccountClaimHelpers";
import {
  getPreferredDefaultCompanyId,
  getPropertyOwnerCompanyId,
  getSelectedCompanyStorageKey,
  selectDefaultCompanyMenuItem,
} from "./DashboardCompanyDefaults";

import type { IMenuItem } from "./DashboardCompanyDefaults";
import type { DashboardNavSlugType } from "../../../services/nav/Types";

interface IProps {
  companyCallback?: (company: ICompanyClaims) => void;
}

const getStoredCompanyId = (zeosUserId?: number | null) => {
  try {
    return localStorage.getItem(getSelectedCompanyStorageKey(zeosUserId));
  } catch {
    return null;
  }
};

const setStoredCompanyId = (companyId: string, zeosUserId?: number | null) => {
  try {
    localStorage.setItem(getSelectedCompanyStorageKey(zeosUserId), companyId);
  } catch {}
};

export const DashboardHeader = (props: IProps) => {
  const log = createLogger("DashboardHeader");
  const auth = useContext(AuthContext);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useSignIn();

  const [searchParams] = useSearchParams();
  const defaultCompanyId = searchParams.get("company");
  const defaultPropertyId = searchParams.get("property");
  const defaultScreeningRun = searchParams.get("screeningRun") ?? searchParams.get("run");

  const [companies, setCompanies] = useState<ICompanyClaims[]>();
  const [companyMenuItems, setCompanyMenuItems] = useState<IMenuItem[]>([]);
  const [currentMenuItem, setCurrentMenuItem] = useState<IMenuItem>();
  const [currentZeosUserId, setCurrentZeosUserId] = useState<number | null>(null);
  const [image, setImage] = useState("");
  const [showScreening, setShowScreening] = useState<boolean>(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [showLogo, setShowLogo] = useState<boolean>(() => {
    const saved = localStorage.getItem("zeos.showLogo");
    return saved ? saved === "true" : false; // default hidden
  });

  const toggleShowLogo = () => {
    setShowLogo((prev) => {
      const next = !prev;
      localStorage.setItem("zeos.showLogo", String(next));
      return next;
    });
  };
  const [userInitials, setUserInitials] = useState<string>("");

  // TODO: AI generated update for menu functionality. Cleanup this code and logic, and if it's usefule, create a reusable component.
  // Menu configuration using key-value pairs
  const menuConfig = {
    companies: {
      title: "Your Companies",
      type: "companies" as const,
      items: companyMenuItems,
    },
    account: {
      title: "Your Account",
      type: "account" as const,
      items: [
        { key: "profile", label: "Profile", action: "Profile" },
        { key: "settings", label: "Settings", action: "Settings" },
        {
          key: "toggleLogo",
          label: showLogo ? "Hide Logo" : "Show Logo",
          action: "ToggleLogo",
        },
        { key: "logout", label: "Logout", action: "Logout" },
      ],
    },
  };

  useEffect(() => {
    const initAsync = async () => {
      const user = await auth.zeosUserAsync();
      setCurrentZeosUserId(user?.zeosUserId ?? null);
      setShowScreening(managesMultiplePortfolios(user));
      if (user?.firstName && user?.lastName) {
        setUserInitials(
          `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
        );
      } else if (user?.emailAddress) {
        setUserInitials(user.emailAddress[0].toUpperCase());
      } else {
        setUserInitials("U");
      }
      const allCompanies = (user?.customerClaims ?? []).flatMap(
        (customer) => customer.companies ?? []
      );
      const sortedCompanies = Array.from(
        new Map(
          allCompanies.map((company) => [company.companyId, company])
        ).values()
      ).sort((a: ICompanyClaims, b: ICompanyClaims) =>
        a.companyName.localeCompare(b.companyName)
      );
      setCompanies(sortedCompanies);
      const menuItems: IMenuItem[] = sortedCompanies.map((company) => ({
        key: company.companyId.toString(),
        label: company.companyName,
        action: company.companyName,
      }));
      setCompanyMenuItems(menuItems);
      const defaultMenuItem = selectDefaultCompanyMenuItem(
        menuItems,
        defaultCompanyId,
        getPreferredDefaultCompanyId(user, sortedCompanies),
        getStoredCompanyId(user?.zeosUserId),
        getPropertyOwnerCompanyId(sortedCompanies, defaultPropertyId)
      );
      if (defaultMenuItem) {
        setStoredCompanyId(defaultMenuItem.key, user?.zeosUserId);
      }
      setCurrentMenuItem(defaultMenuItem);
    };
    initAsync();
  }, [auth, defaultCompanyId, defaultPropertyId]);

  const companyLogoImports = import.meta.glob(
    "../../../assets/company-logos/*",
    { eager: true, import: "default" }
  ) as Record<string, string>;

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setProfileMenuOpen(false);
    //HACK: MUST BE CONVERTED TO DB/BLOB STORAGE
    let imgName;
    switch (currentMenuItem?.key) {
      case "1": //"Sunrise":
        imgName = "sunRise_logo.png";
        break;
      case "2": // "RCD"
        imgName = "logo-rock-city-development.webp";
        break;
      case "3": // "BBL
        imgName = "logo-bbl.webp";
        break;
      case "4": // 1st nat
        imgName = "fnr-logo-3.png";
        break;
      case "5": // huntington
        imgName = "HUNTINGTON-ATRIUM-logo.png";
        break;
      case "6": // roebling
        imgName = "Roebling Lofts.png";
        break;
      case "7": //m holtzman
        imgName = "Holtzman.png";
        break;
    }
    if (imgName) {
      const importedLogo =
        companyLogoImports[`../../../assets/company-logos/${imgName}`];
      setImage(importedLogo ?? "");
    } else {
      setImage("");
    }
    if (props.companyCallback) {
      const activeCompany = companies?.find(
        (x) => x.companyId === Number(currentMenuItem?.key)
      );
      activeCompany && props.companyCallback(activeCompany);
    }
  }, [currentMenuItem]);

  const logoutCallback = () => {
    navigate("/");
  };

  const logoutHandler = async () => {
    await signOut(logoutCallback);
  };

  const handleProfileClick = () => {
    setProfileMenuOpen(!profileMenuOpen);
  };

  const handleCompanyClick = (company: IMenuItem) => {
    setProfileMenuOpen(false);
    setCurrentMenuItem(company);
    setStoredCompanyId(company.key, currentZeosUserId);
    const params = new URLSearchParams();
    params.set("company", company.key);
    navigate(
      {
        pathname: `/${pageConfigBySlug("portfolio")?.slug}`,
        search: `?${params.toString()}`,
      },
      { replace: true }
    );
  };

  const handleOptionClick = (option: string) => {
    setProfileMenuOpen(false);
    if (option === "Logout") {
      zeosConfirmation("Really Sign out?", "Sign Out", async () => {
        await logoutHandler();
      });
    } else if (option === "Profile") {
      navigate("/profile");
    } else if (option === "Settings") {
      navigate("/settings");
    } else if (option === "ToggleLogo") {
      toggleShowLogo();
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setProfileMenuOpen(false);
    }
  };

  const getNavLinkClass = (isActive: boolean, noHover?: boolean) => {
    const baseCss =
      "relative flex items-center justify-center rounded-[4px] py-2 px-4 text-[1.3rem] transition-all duration-300 no-underline ";
    const conditionalCss = isActive
      ? "bg-zeos-orange-dk text-white"
      : noHover
      ? "relative py-2 px-4 cursor-pointer text-zeos-black flex items-center justify-center no-underline text-[1.3rem]"
      : "hover:bg-zeos-orange-dk hover:text-white text-zeos-black";
    return `${baseCss} ${conditionalCss}`;
  };

  const renderNavLink =
    (title: string, alertAmount: number) =>
    ({ isActive }: { isActive: boolean }) =>
      (
        <>
          {title}
          {/* 
          Keep for future use
          {!isActive && (
            <ZeosAlertBadge amount={alertAmount} styles="top-0 right-2" />
          )} */}
        </>
      );

  const buildCompanyPropertyUrl = (slugKey: DashboardNavSlugType) => {
    const params = new URLSearchParams();
    const companyId =
      currentMenuItem?.key || defaultCompanyId || getStoredCompanyId(currentZeosUserId);
    if (companyId) {
      params.set("company", companyId);
    }

    if (defaultScreeningRun && slugKey === "incentives") {
      params.set("screeningRun", defaultScreeningRun);
    } else if (defaultScreeningRun && slugKey === "screening") {
      params.set("run", defaultScreeningRun);
    } else if (defaultPropertyId) {
      params.set("property", defaultPropertyId);
    }

    const search = params.toString();
    return `/${pageConfigBySlug(slugKey)?.slug}${search ? `?${search}` : ""}`;
  };

  type NavItem = {
    key: string;
    title: string;
    slugKey: DashboardNavSlugType;
    disabled: boolean;
    getTo?: () => string;
    isActiveOverride?: (isActive: boolean) => boolean;
  };

  const navItems: NavItem[] = [
    {
      key: "portfolio",
      title: "Portfolio",
      slugKey: "portfolio",
      disabled: false,
      getTo: () => buildCompanyPropertyUrl("portfolio"),
      isActiveOverride: (isActive: boolean) =>
        isActive || location.pathname.startsWith("/dashboard"),
    },
    ...(FeatureFlags.enableExploreNav
      ? [
          {
            key: "explore",
            title: "Explore",
            slugKey: "explore" as DashboardNavSlugType,
            disabled: false,
            getTo: () => buildCompanyPropertyUrl("explore"),
          },
        ]
      : []),
    ...(FeatureFlags.enableComplianceNav
      ? [
          {
            key: "compliance",
            title: "Compliance",
            slugKey: "compliance" as DashboardNavSlugType,
            disabled: false,
            getTo: () => buildCompanyPropertyUrl("compliance"),
          },
        ]
      : []),
    {
      key: "projects",
      title: "Projects",
      slugKey: "projects",
      disabled: false,
      getTo: () => buildCompanyPropertyUrl("projects"),
    },
    // Service Layer tool: only shown to user groups managing more than one portfolio.
    ...(FeatureFlags.enableScreeningNav && showScreening
      ? [
          {
            key: "screening",
            title: "Screening",
            slugKey: "screening" as DashboardNavSlugType,
            disabled: false,
            getTo: () => buildCompanyPropertyUrl("screening"),
          },
        ]
      : []),
    {
      key: "incentives",
      title: "Incentives",
      slugKey: "incentives",
      disabled: false,
      getTo: () => buildCompanyPropertyUrl("incentives"),
    },
  ];

  return (
    <div className="w-full flex items-center justify-between bg-white shadow-md py-2 px-6">
      <div
        className="flex items-center w-1/2 cursor-pointer"
        onClick={() => navigate(buildCompanyPropertyUrl("portfolio"))}
      >
        <div className="flex items-center">
          {showLogo && image ? (
            <img className="h-12" src={image} alt="Logo" />
          ) : (
            <span className="text-2xl font-semibold text-zeos-black">
              {currentMenuItem?.label || "Company"}
            </span>
          )}
        </div>
      </div>
      <div className="h-full flex items-center font-sans gap-x-[15px] w-1/2 justify-end">
        {navItems.map((item) => {
          const to = item.getTo
            ? item.getTo()
            : `/${pageConfigBySlug(item.slugKey)?.slug}`;
          return (
            <NavLink
              key={item.key}
              to={to}
              onClick={item.disabled ? (e) => e.preventDefault() : undefined}
              className={({ isActive }) =>
                getNavLinkClass(
                  item.isActiveOverride
                    ? item.isActiveOverride(isActive)
                    : isActive,
                  item.disabled
                )
              }
            >
              {renderNavLink(item.title, 0)}
            </NavLink>
          );
        })}

        <div className="relative" ref={dropdownRef}>
          <div
            className="size-14 rounded-full border border-zeos-gray-light text-zeos-black flex items-center justify-center cursor-pointer text-xl font-semibold"
            onClick={handleProfileClick}
          >
            {userInitials || ""}
          </div>
          {/* TODO: AI generated update for menu functionality. Cleanup this code and logic, and if it's usefule, create a reusable component. */}
          {profileMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg py-2 z-50">
              {Object.entries(menuConfig).map(([sectionKey, section]) => (
                <div key={sectionKey}>
                  <p className="px-4 py-2 font-semibold text-zeos-black border-b border-gray-200">
                    {section.title}
                  </p>
                  {section.type === "companies"
                    ? section.items.map((company) => (
                        <p
                          key={company.key}
                          className="hover:bg-zeos-orange-dk hover:text-white px-4 py-2 cursor-pointer transition-colors duration-300"
                          onClick={() => handleCompanyClick(company)}
                        >
                          {company.label}
                        </p>
                      ))
                    : section.items.map((item) => (
                        <p
                          key={item.key}
                          className="hover:bg-zeos-orange-dk hover:text-white px-4 py-2 cursor-pointer transition-colors duration-300"
                          onClick={() => handleOptionClick(item.action)}
                        >
                          {item.label}
                        </p>
                      ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
